import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeading, PageShell } from "@/components/layout/PageShell";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { CardSkeletonGrid, EmptyState, ErrorState } from "@/components/states";
import { useI18n } from "@/lib/i18n";
import { branchesQuery } from "@/lib/queries";
import { formatHours, loc } from "@/lib/format";

export const Route = createFileRoute("/branchMenu/")({
  component: BranchMenuSelect,
});

function BranchMenuSelect() {
  const { t, lang } = useI18n();
  const { data, error, isPending, isError, refetch } = useQuery(branchesQuery());
  const branches = (data ?? []).filter((b) => b.indoorService);

  return (
    <PageShell>
      <PageHeading
        breadcrumbs={[{ label: t("nav.home"), to: "/" }, { label: t("nav.menu"), to: "/main-menu" }, { label: t("branchMenu.selectTitle") }]}
        back
        backTo="/main-menu"
        title={t("branchMenu.selectTitle")}
        description={t("branchMenu.selectDesc")}
      />

      {isPending ? (
        <CardSkeletonGrid count={3} />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : branches.length === 0 ? (
        <EmptyState title={t("branches.noResults")} />
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {branches.map((b) => (
            <Link
              key={b.id}
              to="/branchMenu/$branchId"
              params={{ branchId: b.id }}
              className="surface-card group overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lift"
            >
              <ResponsiveImage
                source={b.responsiveImage ?? b.image}
                sizes="(max-width: 639px) calc(100vw - 2rem), (max-width: 1023px) 50vw, 33vw"
                alt=""
                loading="lazy"
                className="h-44 w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="grid gap-1 p-5">
                <h2 className="text-lg font-bold">{loc(b.name, lang)}</h2>
                <p className="text-sm text-muted-foreground">{loc(b.address, lang)}</p>
                <p className="text-xs font-bold text-primary">
                  {formatHours(b.openingHours, lang)} ·{" "}
                  {b.openNow ? t("branches.openNow") : t("branches.closedNow")}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PageShell>
  );
}
